import type { ActivityLine } from "../../server/rules/activity";
import { api, el, tipHide, toast } from "./dom";
import { icon } from "./icons";
import { openModal } from "./modal";

// The activity panel (brief 1A-6, mockup .actpanel): a tab at the bottom right, "Activity" with
// the number of changes, that opens the record's history (GET /api/activity/:kind/:id): what
// changed, who and when, newest first. At phone width it opens as a sheet along the bottom.
// A client or person shows its open flags and blocks above the history (brief 1B-7, mockup
// "Flags on this record"); a block shows Release to people allowed to, asking for the reason.

/** One open flag or block, as GET /api/activity answers it. */
interface OpenFlag {
  id: string;
  level: "flag" | "block";
  levelLabel: string;
  message: string;
  opened: string;
  holdsMoney: boolean;
  canRelease: boolean;
}
interface ActivityAnswer {
  lines: ActivityLine[];
  flags?: OpenFlag[];
  releaseReasons?: string[];
}

/** Asks why a block is let go (mockup release modal), then releases it. */
function releaseModal(f: OpenFlag, reasons: readonly string[], done: () => void) {
  const why = el("select", { id: "rl-why" }, ...reasons.map((r) => el("option", { value: r }, r)));
  const note = el("textarea", { id: "rl-note", rows: "3" });
  const go = el("button", { type: "button", class: "btn pri" }, icon("unlock"), " Release");
  const m = openModal({
    title: "Release this block",
    sub: `${f.message}.`,
    body: [
      el(
        "div",
        { class: "form", style: "max-width:none" },
        el("div", { class: "field" }, el("label", { for: "rl-why" }, "Why"), why),
        el("div", { class: "field wide" }, el("label", { for: "rl-note" }, "Note"), note),
        el("p", { class: "small muted wide" }, "It goes on the fix list in Settings › Workflows."),
      ),
    ],
    actions: [go],
  });
  why.focus();
  go.addEventListener("click", async () => {
    go.disabled = true;
    const res = await api("POST", `/api/flags/${encodeURIComponent(f.id)}/release`, {
      reason: why.value,
      note: note.value,
    });
    go.disabled = false;
    if (!res.ok) {
      m.note.hidden = false;
      m.note.textContent =
        (res.body as { error?: string }).error ?? "It wasn't released. Try again.";
      return;
    }
    m.close();
    toast("Block released", "It's on the fix list");
    done();
  });
}

export interface ActivityPanel {
  el: HTMLElement;
  /** Asks the server again (after a save), keeping the panel open or shut as it was. Opening
   * the panel asks again too. */
  refresh: () => Promise<void>;
}

export function activityPanel(
  kind: string,
  id: string,
  opts: { autosaved?: boolean } = {},
): ActivityPanel {
  const aside = el("aside", { class: "actpanel", "aria-label": "Activity" });
  let lines: ActivityLine[] = [];
  let flags: OpenFlag[] = [];
  let reasons: string[] = [];
  let error: string | null = null;
  let open = false;

  const tab = el("button", {
    type: "button",
    class: "acttab",
    "aria-expanded": "false",
  });
  tab.addEventListener("click", () => setOpen(true));

  function setOpen(want: boolean) {
    open = want;
    tipHide();
    draw();
    if (open) {
      aside.querySelector<HTMLElement>(".actbody h2")?.focus();
      // Opening always asks again, so a change saved since the last look is there.
      void refresh();
    } else tab.focus();
  }

  aside.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(false);
    }
  });

  function draw() {
    aside.classList.toggle("open", open);
    tab.setAttribute("aria-expanded", String(open));
    tab.replaceChildren(
      icon("history"),
      " Activity",
      ...(lines.length ? [" ", el("span", { class: "cnt" }, String(lines.length))] : []),
      ...(flags.length
        ? [
            " ",
            el(
              "span",
              { class: "cnt bad", "aria-label": `${flags.length} open flags` },
              String(flags.length),
            ),
          ]
        : []),
    );
    if (!open) {
      // A late answer redraws the shut panel; moving the tab would drop its focus.
      if (aside.childElementCount !== 1 || aside.firstChild !== tab) aside.replaceChildren(tab);
      return;
    }
    const close = el(
      "button",
      { type: "button", class: "btn ib sm", "aria-label": "Close", "data-tip": "Close" },
      icon("x"),
    );
    close.addEventListener("click", () => setOpen(false));
    const history = error
      ? el("p", { class: "note", role: "alert" }, error)
      : lines.length
        ? el(
            "ol",
            { class: "actlist" },
            ...lines.map((l) =>
              el(
                "li",
                {},
                el("b", {}, l.what),
                l.detail ? el("span", {}, l.detail) : null,
                el("small", {}, `${l.who} · ${l.when}`),
              ),
            ),
          )
        : el(
            "p",
            { class: "muted small" },
            "No changes yet. Each change shows here with who and when.",
          );
    aside.replaceChildren(
      tab,
      el(
        "div",
        { class: "actbody", role: "region", "aria-label": "Activity" },
        el(
          "div",
          { class: "cardhead", style: "margin:0 0 6px" },
          el("h2", { tabindex: "-1" }, "Activity"),
          close,
        ),
        opts.autosaved
          ? el("p", { class: "small saved" }, icon("check-circle"), " Saved automatically")
          : null,
        ...(flags.length
          ? [
              el("h3", { class: "h3s" }, "Flags on this record"),
              el(
                "ul",
                { class: "flaglist", "aria-label": "Flags on this record" },
                ...flags.map((f) => {
                  const release = f.canRelease
                    ? el("button", { type: "button", class: "btn sm" }, icon("unlock"), " Release")
                    : null;
                  release?.addEventListener("click", () =>
                    releaseModal(f, reasons, () => void refresh()),
                  );
                  return el(
                    "li",
                    {},
                    el(
                      "span",
                      { class: `chip ${f.level === "block" ? "bad" : "warn"}` },
                      f.level === "block" ? "Block · bill or pay held" : f.levelLabel,
                    ),
                    el("b", {}, f.message),
                    el("small", {}, `Since ${f.opened}`),
                    release,
                  );
                }),
              ),
            ]
          : []),
        el("h3", { class: "h3s" }, "History"),
        history,
      ),
    );
  }

  // Only the newest ask is drawn; an older answer arriving late is dropped.
  let asked = 0;
  async function refresh() {
    const n = ++asked;
    const res = await api<ActivityAnswer>(
      "GET",
      `/api/activity/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`,
    );
    if (n !== asked) return;
    if (res.ok) {
      lines = res.body.lines;
      flags = res.body.flags ?? [];
      reasons = res.body.releaseReasons ?? [];
      error = null;
    } else {
      error = "The history couldn't be loaded.";
    }
    const had = aside.contains(document.activeElement) ? document.activeElement : null;
    draw();
    if (had && !aside.contains(had))
      (open ? aside.querySelector<HTMLElement>(".actbody h2") : tab)?.focus();
  }

  draw();
  void refresh();
  return { el: aside, refresh };
}
